import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

// Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ FIXED: Fetch user profile using ID instead of email
  const fetchUserProfile = useCallback(async (authUser) => {
    if (!authUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Query by ID instead of email (matches RLS policy)
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();  // Use maybeSingle instead of single to avoid errors

      if (error) {
        console.error('Error fetching user:', error);
        
        // If user doesn't exist, create them
        if (error.code === 'PGRST116' || !data) {
          const newUser = {
            id: authUser.id,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
            role: 'member',
            created_at: new Date().toISOString()
          };
          
          const { data: createdUser, error: createError } = await supabase
            .from('users')
            .insert([newUser])
            .select()
            .single();

          if (createError) {
            console.error('Error creating user profile:', createError);
            // Still set basic user info so they can access the app
            setUser({
              id: authUser.id,
              email: authUser.email,
              full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
              role: 'member'
            });
          } else {
            setUser(createdUser);
          }
        } else {
          // Set basic user from auth if we can't fetch profile
          setUser({
            id: authUser.id,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
            role: 'member'
          });
        }
      } else if (data) {
        setUser(data);
      } else {
        // No data found, create new user
        const newUser = {
          id: authUser.id,
          email: authUser.email,
          full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
          role: 'member',
          created_at: new Date().toISOString()
        };
        
        const { data: createdUser, error: createError } = await supabase
          .from('users')
          .insert([newUser])
          .select()
          .single();

        if (createError && createError.code !== '23505') {
          console.error('Error creating user profile:', createError);
          setUser({
            id: authUser.id,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
            role: 'member'
          });
        } else if (createdUser) {
          setUser(createdUser);
        } else {
          setUser({
            id: authUser.id,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
            role: 'member'
          });
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      // Fallback: set basic user info
      setUser({
        id: authUser.id,
        email: authUser.email,
        full_name: authUser.user_metadata?.full_name || authUser.email.split('@')[0],
        role: 'member'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for auth changes
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchUserProfile(session.user);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          fetchUserProfile(session.user);
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchUserProfile]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data.user;
  };

  const register = async (email, password, full_name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: full_name,
        },
      },
    });

    if (error) throw error;

    if (data.user) {
      const { error: profileError } = await supabase
        .from('users')
        .insert([{
          id: data.user.id,
          email: email,
          full_name: full_name,
          role: 'member',
          created_at: new Date().toISOString()
        }]);

      if (profileError && profileError.code !== '23505') {
        console.error('Error creating profile:', profileError);
      }
    }

    return data.user;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
    return { message: 'Password reset email sent' };
  };

  const resetPassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
    return { message: 'Password updated successfully' };
  };

  // API helper for database operations
  const api = {
    getMemberCount: async () => {
      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });
      if (error) {
        console.error('Error fetching member count:', error);
        return 0;
      }
      return count || 0;
    },

    getResources: async (filters = {}) => {
      let query = supabase.from('resources').select('*').order('created_at', { ascending: false });
      if (filters.category) query = query.eq('category', filters.category);
      if (filters.level) query = query.eq('level', filters.level);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    createResource: async (resource) => {
      const { data, error } = await supabase.from('resources').insert([resource]).select().single();
      if (error) throw error;
      return data;
    },

    deleteResource: async (id) => {
      const { error } = await supabase.from('resources').delete().eq('id', id);
      if (error) throw error;
    },

    getVacancies: async () => {
      const { data, error } = await supabase.from('vacancies').select('*').eq('is_active', true).order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },

    createVacancy: async (vacancy) => {
      const { data, error } = await supabase.from('vacancies').insert([{ ...vacancy, is_active: true }]).select().single();
      if (error) throw error;
      return data;
    },

    deleteVacancy: async (id) => {
      const { error } = await supabase.from('vacancies').delete().eq('id', id);
      if (error) throw error;
    },

    getSuggestions: async () => {
      const { data, error } = await supabase.from('suggestions').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },

    createSuggestion: async (suggestion) => {
      const { data, error } = await supabase.from('suggestions').insert([suggestion]).select().single();
      if (error) throw error;
      return data;
    },

    markSuggestionRead: async (id) => {
      const { error } = await supabase.from('suggestions').update({ is_read: true }).eq('id', id);
      if (error) throw error;
    },

    getWelfare: async () => {
      const { data, error } = await supabase.from('welfare').select('*').eq('is_active', true).order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },

    createWelfare: async (welfare) => {
      const { data, error } = await supabase.from('welfare').insert([{ ...welfare, is_active: true }]).select().single();
      if (error) throw error;
      return data;
    },

    deleteWelfare: async (id) => {
      const { error } = await supabase.from('welfare').delete().eq('id', id);
      if (error) throw error;
    },

    getArticles: async (featuredOnly = false) => {
      let query = supabase.from('articles').select('*').order('created_at', { ascending: false });
      if (featuredOnly) query = query.eq('is_featured', true);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    createArticle: async (article) => {
      const { data, error } = await supabase.from('articles').insert([article]).select().single();
      if (error) throw error;
      return data;
    },

    deleteArticle: async (id) => {
      const { error } = await supabase.from('articles').delete().eq('id', id);
      if (error) throw error;
    },

    getAdvertisements: async (approvedOnly = true) => {
      let query = supabase.from('advertisements').select('*').order('created_at', { ascending: false });
      if (approvedOnly) query = query.eq('is_approved', true).eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    getAllAdvertisements: async () => {
      const { data, error } = await supabase
        .from('advertisements')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },

    createAdvertisement: async (ad) => {
      const { data, error } = await supabase
        .from('advertisements')
        .insert([{
          title: ad.title,
          description: ad.description || null,
          business_name: ad.business_name,
          category: ad.category || 'other',
          image_url: ad.image_url || null,
          website: ad.website || null,
          email: ad.email || null,
          phone: ad.phone || null,
          location: ad.location || null,
          is_active: true,
          is_approved: true
        }])
        .select()
        .single();
      
      if (error) {
        console.error('Supabase create ad error:', error);
        throw error;
      }
      return data;
    },

    updateAdvertisement: async (id, updates) => {
      const { data, error } = await supabase
        .from('advertisements')
        .update({
          title: updates.title,
          description: updates.description || null,
          business_name: updates.business_name,
          category: updates.category || 'other',
          image_url: updates.image_url || null,
          website: updates.website || null,
          email: updates.email || null,
          phone: updates.phone || null,
          location: updates.location || null,
          is_active: updates.is_active ?? true,
          is_approved: updates.is_approved ?? true
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Supabase update ad error:', error);
        throw error;
      }
      return data;
    },

    approveAdvertisement: async (id) => {
      const { error } = await supabase
        .from('advertisements')
        .update({ is_approved: true })
        .eq('id', id);
      if (error) throw error;
    },

    rejectAdvertisement: async (id) => {
      const { error } = await supabase
        .from('advertisements')
        .update({ is_approved: false })
        .eq('id', id);
      if (error) throw error;
    },

    toggleAdvertisement: async (id, isActive) => {
      const { error } = await supabase
        .from('advertisements')
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
    },

    deleteAdvertisement: async (id) => {
      const { error } = await supabase.from('advertisements').delete().eq('id', id);
      if (error) throw error;
    },

    getAdInquiries: async () => {
      const { data, error } = await supabase.from('ad_inquiries').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },

    createAdInquiry: async (inquiry) => {
      const { data, error } = await supabase.from('ad_inquiries').insert([{ ...inquiry, status: 'pending' }]).select().single();
      if (error) throw error;
      return data;
    },

    updateInquiryStatus: async (id, status) => {
      const { error } = await supabase.from('ad_inquiries').update({ status }).eq('id', id);
      if (error) throw error;
    },

    deleteAdInquiry: async (id) => {
      const { error } = await supabase.from('ad_inquiries').delete().eq('id', id);
      if (error) throw error;
    },

    getUsers: async () => {
      const { data, error } = await supabase.from('users').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },

    updateUserRole: async (id, role) => {
      const { error } = await supabase.from('users').update({ role }).eq('id', id);
      if (error) throw error;
    },

    deleteUser: async (id) => {
      const { error } = await supabase.from('users').delete().eq('id', id);
      if (error) throw error;
    },

    getStats: async () => {
      const [resources, vacancies, suggestions, members, activeAds, pendingAds, inquiries] = await Promise.all([
        supabase.from('resources').select('id', { count: 'exact' }),
        supabase.from('vacancies').select('id', { count: 'exact' }).eq('is_active', true),
        supabase.from('suggestions').select('id', { count: 'exact' }).eq('is_read', false),
        supabase.from('users').select('id', { count: 'exact' }).eq('role', 'member'),
        supabase.from('advertisements').select('id', { count: 'exact' }).eq('is_active', true).eq('is_approved', true),
        supabase.from('advertisements').select('id', { count: 'exact' }).eq('is_approved', false),
        supabase.from('ad_inquiries').select('id', { count: 'exact' }).eq('status', 'pending'),
      ]);

      return {
        resources: resources.count || 0,
        vacancies: vacancies.count || 0,
        unread_suggestions: suggestions.count || 0,
        members: members.count || 0,
        active_ads: activeAds.count || 0,
        pending_ads: pendingAds.count || 0,
        pending_ad_inquiries: inquiries.count || 0,
      };
    },
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    api,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
